/**
 * Tpt Announcement Bar JavaScript
 */

if (!customElements.get('tpt-announcement-bar')) {
    class TptAnnouncementBar {
      constructor(element) {
        this.element = element;
        this.announcements = element.querySelectorAll('.tpt-announcement-bar_announcement');
        this.prevButton = element.querySelector('.tpt-announcement-bar_nav-prev');
        this.nextButton = element.querySelector('.tpt-announcement-bar_nav-next');
        this.dots = element.querySelectorAll('.tpt-announcement-bar_dot');
        this.currentIndex = 0;
        this.totalAnnouncements = parseInt(element.getAttribute('data-total-announcements'), 10) || 1;
        this.autoRotate = element.getAttribute('data-auto-rotate') === 'true';
        this.autoRotateDelay = parseInt(element.style.getPropertyValue('--auto-rotate-delay').replace('s', ''), 10) * 1000 || 5000;
        this.autoRotateTimer = null;
        this.isHovered = false;
  
        this.init();
      }
  
      init() {
        if (this.totalAnnouncements <= 1) return;
  
        this.bindEvents();
        this.setupKeyboardNavigation();
        
        if (this.autoRotate) {
          this.startAutoRotate();
        }
      }
  
      bindEvents() {
        // Navigation buttons
        if (this.prevButton) {
          this.prevButton.addEventListener('click', () => this.goToPrevious());
        }
        
        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.goToNext());
        }
  
        // Dot navigation
        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.goToSlide(index));
        });
  
        // Pause auto-rotate on hover
        this.element.addEventListener('mouseenter', () => {
          this.isHovered = true;
          this.stopAutoRotate();
        });
  
        this.element.addEventListener('mouseleave', () => {
          this.isHovered = false;
          if (this.autoRotate) {
            this.startAutoRotate();
          }
        });
  
        // Pause auto-rotate when focused
        this.element.addEventListener('focusin', () => {
          this.stopAutoRotate();
        });
  
        this.element.addEventListener('focusout', () => {
          if (this.autoRotate && !this.isHovered) {
            this.startAutoRotate();
          }
        });
  
        // Touch/swipe support
        this.setupTouchEvents();
      }
  
      setupKeyboardNavigation() {
        this.element.addEventListener('keydown', (e) => {
          switch (e.key) {
            case 'ArrowLeft':
              e.preventDefault();
              this.goToPrevious();
              break;
            case 'ArrowRight':
              e.preventDefault();
              this.goToNext();
              break;
            case 'Home':
              e.preventDefault();
              this.goToSlide(0);
              break;
            case 'End':
              e.preventDefault();
              this.goToSlide(this.totalAnnouncements - 1);
              break;
          }
        });
      }
  
      setupTouchEvents() {
        let startX = 0;
        let startY = 0;
        let endX = 0;
        let endY = 0;
  
        this.element.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        }, { passive: true });
  
        this.element.addEventListener('touchmove', (e) => {
          endX = e.touches[0].clientX;
          endY = e.touches[0].clientY;
        }, { passive: true });
  
        this.element.addEventListener('touchend', () => {
          const deltaX = startX - endX;
          const deltaY = startY - endY;
          const minSwipeDistance = 50;
  
          // Only trigger swipe if horizontal movement is greater than vertical
          if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
            if (deltaX > 0) {
              // Swipe left - go to next
              this.goToNext();
            } else {
              // Swipe right - go to previous
              this.goToPrevious();
            }
          }
        }, { passive: true });
      }
  
      goToNext() {
        const nextIndex = (this.currentIndex + 1) % this.totalAnnouncements;
        this.goToSlide(nextIndex);
      }
  
      goToPrevious() {
        const prevIndex = (this.currentIndex - 1 + this.totalAnnouncements) % this.totalAnnouncements;
        this.goToSlide(prevIndex);
      }
  
      goToSlide(index) {
        if (index === this.currentIndex || index < 0 || index >= this.totalAnnouncements) {
          return;
        }
  
        // Update announcements
        this.announcements[this.currentIndex].classList.remove('tpt-announcement-bar_announcement-active');
        this.announcements[index].classList.add('tpt-announcement-bar_announcement-active');
  
        // Update dots
        if (this.dots.length > 0) {
          this.dots[this.currentIndex].classList.remove('tpt-announcement-bar_dot-active');
          this.dots[index].classList.add('tpt-announcement-bar_dot-active');
        }
  
        this.currentIndex = index;
  
        // Restart auto-rotate timer
        if (this.autoRotate && !this.isHovered) {
          this.restartAutoRotate();
        }
  
        // Announce to screen readers
        this.announceToScreenReader();
      }
  
      startAutoRotate() {
        if (this.totalAnnouncements <= 1) return;
        
        this.stopAutoRotate();
        this.autoRotateTimer = setInterval(() => {
          this.goToNext();
        }, this.autoRotateDelay);
      }
  
      stopAutoRotate() {
        if (this.autoRotateTimer) {
          clearInterval(this.autoRotateTimer);
          this.autoRotateTimer = null;
        }
      }
  
      restartAutoRotate() {
        if (this.autoRotate) {
          this.stopAutoRotate();
          this.startAutoRotate();
        }
      }
  
      announceToScreenReader() {
        // Create or update screen reader announcement
        let announcement = this.element.querySelector('.sr-only-announcement');
        if (!announcement) {
          announcement = document.createElement('div');
          announcement.className = 'sr-only-announcement';
          announcement.style.cssText = 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0;';
          announcement.setAttribute('aria-live', 'polite');
          announcement.setAttribute('aria-atomic', 'true');
          this.element.appendChild(announcement);
        }
        
        announcement.textContent = `Announcement ${this.currentIndex + 1} of ${this.totalAnnouncements}`;
      }
  
      destroy() {
        this.stopAutoRotate();
        // Remove event listeners would go here if needed
      }
    }
  
    // Initialize announcement bars
    document.addEventListener('DOMContentLoaded', () => {
      const announcementBars = document.querySelectorAll('.tpt-announcement-bar');
      announcementBars.forEach(bar => {
        new TptAnnouncementBar(bar);
      });
    });
  
    // Handle dynamic content (like when sections are loaded via AJAX)
    document.addEventListener('shopify:section:load', () => {
      const announcementBars = document.querySelectorAll('.tpt-announcement-bar');
      announcementBars.forEach(bar => {
        if (!bar.classList.contains('tpt-announcement-bar-initialized')) {
          bar.classList.add('tpt-announcement-bar-initialized');
          new TptAnnouncementBar(bar);
        }
      });
    });
  }